import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fromType, SortField } from "@src/store/helpers";
import { getThunkAndSlice } from "@src/store/getThunkAndSlice";

export interface QuerySalesParamsDto {
	searchString?: string | null;
	categoryId?: number | null;
	eventId?: number | null;
	eventHeadlinerId?: number | null;
	venueId?: number | null;
	saleFromDate?: string | null;
	saleToDate?: string | null;
	eventFromDate?: string | null;
	eventToDate?: string | null;
	paymentFromDate?: string | null;
	paymentToDate?: string | null;
	exchangeName?: string | null;
	saleStatus?: string | null;
	invoiceIds?: Array<number>;
	sections?: string | null;
	rows?: string | null;
	quantity?: number | null;
	companyName?: string | null;
	forceRefresh?: boolean;
	SearchTag?: string | null;
	buyerPOID?: string | null;
	invoiceId?: number | null;
	page?: number | null;
	startIndex?: number | null;
	limit?: number | null;
	orderByColumn?: Array<SortField>;
}

export interface SalesInfo {
	Airbill_Date: string | null;
	Airbill_User: string | null;
	Automation_Notes: string | null;
	Buyer_PO_ID: string | null;
	Cancelled_Date: string | null;
	Cat_Desc: string | null;
	Commission_Amount: number | null;
	Commission_Rate: number | null;
	Company_Name: string;
	Completed_Date: string | null;
	Cost_Locked: boolean | null;
	Customer_Name?: string | null;
	Event_ID: number | null;
	Event_Name: string | null;
	Event_Headliner: string | null;
	Event_Date: string | null;
	Event_Time: string | null;
	Event_Tags: string | null;
	Exchange_Name: string;
	External_Notes: string;
	InHand: boolean | null;
	InHandDate: string | null;
	Internal_Notes: string;
	Invoice_Amount: number;
	Invoice_ID: number;
	Is_eDelivery: boolean;
	Item_Price: number;
	Item_Tags: string | null;
	Listing_ID: number;
	Num_Barcodes: number;
	Num_PDFs: number;
	PO_Date: string | null;
	PO_ID: number;
	Payment_Amount: number | null;
	Payment_Date: string | null;
	Payment_Details: string | null;
	Payment_Notes: string | null;
	Payment_Type: string | null;
	PdfsAttached: number;
	Quantity: number;
	ROI: number;
	ROIPercent: number;
	Row: string;
	Sale_Date: string | null;
	Sale_Time: string | null;
	Sale_Status: string;
	Sale_Type: string;
	Seat_From: number | null;
	Seat_Thru: number | null;
	Section: string;
	Shipping_Comments: string | null;
	Tickets_Available_Date: string | null;
	Total_Cost: number;
	Venue_City: string;
	Venue_Name: string;
	Venue_State: string;
	Estimated_Payment_Date: string | null;
	Estimated_Payment_Display: string | null;
	Estimated_Payment_Sort_Group: number;
}

interface PagedSalesInfo {
	itemCount: number;
	SalesInfo: Array<SalesInfo>;
	defaultSaleStartDate: string | null;
}

const initialState = fromType<PagedSalesInfo>({
	itemCount: 0,
	SalesInfo: [],
	defaultSaleStartDate: null,
});

export const {
	thunk: querySales,
	slice: hiddenQuerySalesSlice,
	extraReducers: hiddenQuerySalesExtraReducers,
} = getThunkAndSlice<QuerySalesParamsDto, typeof initialState>({
	initialState,
	path: "/api/Sales/QuerySales",
	method: "post",
	onResponseHeaders: (headers, data) => {
		const defaultSaleStartDate = headers["x-default-sale-start-date"];
		return { ...data, defaultSaleStartDate: typeof defaultSaleStartDate === "string" ? defaultSaleStartDate : null };
	},
});

export interface IAppliedPayment {
	invoiceIds: Array<number>;
	paymentDate: string;
	paymentType: string;
	paymentDetails: string;
	paymentNotes: string;
	saleStatus?: string;
}

export const querySalesSlice = createSlice({
	name: hiddenQuerySalesSlice.name,
	initialState: hiddenQuerySalesSlice.getInitialState(),
	reducers: {
		applyPaymentToSales: (state, action: PayloadAction<IAppliedPayment>) => {
			const { invoiceIds, paymentDate, paymentType, paymentDetails, paymentNotes, saleStatus } = action.payload;
			const targetIds = new Set(invoiceIds);

			state.data.SalesInfo = state.data.SalesInfo.map((sale) => {
				if (!targetIds.has(sale.Invoice_ID)) return sale;
				return {
					...sale,
					Payment_Date: paymentDate,
					Payment_Type: paymentType,
					Payment_Details: paymentDetails,
					Payment_Notes: paymentNotes,
					Sale_Status: saleStatus ?? sale.Sale_Status,
				};
			});
			state.version++;
		},
		removeCancelledInvoices: (state, action: PayloadAction<Array<number>>) => {
			const targetIds = new Set(action.payload);

			state.data.SalesInfo = state.data.SalesInfo.filter((sale) => !targetIds.has(sale.Invoice_ID));
			state.data.itemCount = state.data.SalesInfo.length;
			state.version++;
		},
		updateSaleCost: (state, action: PayloadAction<{ invoiceId: number; itemCost: number }>) => {
			const { invoiceId, itemCost } = action.payload;

			state.data.SalesInfo = state.data.SalesInfo.map((sale) => {
				if (sale.Invoice_ID !== invoiceId) return sale;
				const Total_Cost = itemCost * sale.Quantity;
				const ROI = (sale.Payment_Amount ?? 0) - Total_Cost;
				return { ...sale, Total_Cost, ROI, ROIPercent: Total_Cost ? ROI / Total_Cost : 0 };
			});
			state.version++;
		},
		removeAdjustmentRows: (state, action: PayloadAction<Array<number>>) => {
			const targetIds = new Set(action.payload);

			state.data.SalesInfo = state.data.SalesInfo.filter((sale) => !targetIds.has(sale.Invoice_ID));
			state.data.itemCount = state.data.SalesInfo.length;
			state.version++;
		},
	},
	extraReducers: hiddenQuerySalesExtraReducers,
});
